/** CSS custom property names consumed by the canvas feature (defined per consuming app). */
export const CANVAS_COLOR_VARS = {
  primary: '--canvas-primary',
  primaryHover: '--canvas-primary-hover',
  primaryDeeper: '--canvas-primary-deeper',
  primaryDeepest: '--canvas-primary-deepest',
  primaryBg: '--canvas-primary-bg',
  primaryBgFaint: '--canvas-primary-bg-faint',
  primaryBgSoft: '--canvas-primary-bg-soft',
  primaryBorder: '--canvas-primary-border',
  primaryBorderMid: '--canvas-primary-border-mid',
  primaryBorderActive: '--canvas-primary-border-active',
  action: '--canvas-action',
  actionHover: '--canvas-action-hover',
  actionDeep: '--canvas-action-deep',
  map: '--canvas-map',
  mapHover: '--canvas-map-hover',
  mapLayerActive: '--canvas-map-layer-active',
  chartBrand: '--canvas-chart-brand',
  chartTextDark: '--canvas-chart-text-dark',
  chartTextDeep: '--canvas-chart-text-deep',
  chartTextDarkest: '--canvas-chart-text-darkest',
  checkboxAccent: '--canvas-checkbox-accent',
  boundaryStroke: '--canvas-boundary-stroke',
  boundaryFill: '--canvas-boundary-fill',
  boundaryStrokeDark: '--canvas-boundary-stroke-dark',
  boundaryFillDark: '--canvas-boundary-fill-dark',
  basemapLightStroke: '--canvas-basemap-light-stroke',
  basemapLightFill: '--canvas-basemap-light-fill',
  basemapDarkBg: '--canvas-basemap-dark-bg',
  maskFill: '--canvas-mask-fill',
  bboxStroke: '--canvas-bbox-stroke',
  bboxFill: '--canvas-bbox-fill',
  ogcStroke: '--canvas-ogc-stroke',
  ogcFill: '--canvas-ogc-fill',
  measureStroke: '--canvas-measure-stroke',
  measureVertexFill: '--canvas-measure-vertex-fill',
  measureVertexStroke: '--canvas-measure-vertex-stroke',
  shapeDataset: '--canvas-shape-dataset',
  shapeCircle: '--canvas-shape-circle',
  shapeRectangle: '--canvas-shape-rectangle',
  shapePolygon: '--canvas-shape-polygon',
  shapeDefault: '--canvas-shape-default',
  shapeDatasetBg: '--canvas-shape-dataset-bg',
  shapeCircleBg: '--canvas-shape-circle-bg',
  shapeRectangleBg: '--canvas-shape-rectangle-bg',
  shapePolygonBg: '--canvas-shape-polygon-bg',
  shapeDefaultBg: '--canvas-shape-default-bg',
  layerDefault: '--canvas-layer-default',
  markerDefault: '--canvas-marker-default',
  markerFallbackBlue: '--canvas-marker-fallback-blue',
  danger: '--canvas-danger',
  dangerHover: '--canvas-danger-hover',
  dangerBg: '--canvas-danger-bg',
  dangerFaint: '--canvas-danger-faint',
  warning: '--canvas-warning',
  warningBg: '--canvas-warning-bg',
  surface: '--canvas-surface',
  surfaceMuted: '--canvas-surface-muted',
  surfaceSubtle: '--canvas-surface-subtle',
  surfaceFaint: '--canvas-surface-faint',
  surfaceCard: '--canvas-surface-card',
  border: '--canvas-border',
  borderMuted: '--canvas-border-muted',
  borderSoft: '--canvas-border-soft',
  borderLighter: '--canvas-border-lighter',
  text: '--canvas-text',
  textBody: '--canvas-text-body',
  textSecondary: '--canvas-text-secondary',
  textMuted: '--canvas-text-muted',
  textFaint: '--canvas-text-faint',
  overlay: '--canvas-overlay',
  overlayLight: '--canvas-overlay-light',
  overlayMedium: '--canvas-overlay-medium',
  tooltipBg: '--canvas-tooltip-bg',
  accessPrimary: '--canvas-access-primary',
  chartBg: '--canvas-chart-bg',
  chartZoomBg: '--canvas-chart-zoom-bg',
  chartZoomBorder: '--canvas-chart-zoom-border',
  chartZoomFiller: '--canvas-chart-zoom-filler',
  chartZoomSelected: '--canvas-chart-zoom-selected',
  chartAxisLine: '--canvas-chart-axis-line',
  chartAxisLabel: '--canvas-chart-axis-label',
  chartSplitLine: '--canvas-chart-split-line',
  chartDataLine: '--canvas-chart-data-line',
  chartDataArea: '--canvas-chart-data-area',
  chartNameText: '--canvas-chart-name-text',
  chart1: '--canvas-chart-1',
  chart2: '--canvas-chart-2',
  chart3: '--canvas-chart-3',
  chart4: '--canvas-chart-4',
  chart5: '--canvas-chart-5',
  chart6: '--canvas-chart-6',
  chart7: '--canvas-chart-7',
  chart8: '--canvas-chart-8',
} as const;

export type CanvasColorVar = (typeof CANVAS_COLOR_VARS)[keyof typeof CANVAS_COLOR_VARS];

/**
 * Read a canvas theme color from the document `:root`.
 * Consuming apps must define all `--canvas-*` variables in `canvas-variables.scss`.
 */
export function getCanvasColor(varName: CanvasColorVar | string): string {
  if (typeof document === 'undefined') {
    return '';
  }
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

/** Series palette for ECharts (reads --canvas-chart-1 … --canvas-chart-8). */
export function getCanvasChartColors(): string[] {
  return [
    CANVAS_COLOR_VARS.chart1,
    CANVAS_COLOR_VARS.chart2,
    CANVAS_COLOR_VARS.chart3,
    CANVAS_COLOR_VARS.chart4,
    CANVAS_COLOR_VARS.chart5,
    CANVAS_COLOR_VARS.chart6,
    CANVAS_COLOR_VARS.chart7,
    CANVAS_COLOR_VARS.chart8,
  ].map(getCanvasColor);
}
